import { useCallback, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import { FaTimes } from 'react-icons/fa'
import styled from 'styled-components'
// store
import { useAppDispatch, useAppSelector } from 'appState/store'
import { updateDialPadVisibility } from 'features/calls/callsSlice'
// components
import { DialPadButton } from 'features/calls/components'
// misc
import { softphone } from 'features/calls/utils/softphone'
import { dialPadItems } from './dialPadItems'

const DialPad = () => {
  // global state
  const { isConnected } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)

  // local state
  const [dialed, setDialed] = useState<string>('')
  const draggableRef = useRef(null)

  // store
  const dispatch = useAppDispatch()

  // handlers
  const handleCloseDialpad = () => {
    setDialed('')
    dispatch(updateDialPadVisibility(false))
  }

  const handleClick = (pad: string) => () => {
    if (pad) {
      setDialed((prev) => prev + pad)
      isConnected && softphone.sendDTMFCode(pad)
    }
  }

  const renderDialPadItems = useCallback(() => {
    return dialPadItems.map(({ title, subtitle }) => (
      <DialPadButton key={title} title={title} subtitle={subtitle} onClick={handleClick(title)} />
    ))
  }, [dialPadItems])

  return (
    <Draggable nodeRef={draggableRef} handle=".drag" bounds="body">
      <Div ref={draggableRef}>
        <section className="dialpad-header drag">
          <span onClick={handleCloseDialpad}>
            <FaTimes />
          </span>
        </section>
        <section className="dialpad-body">
          <section className="dialpad-input-container">
            <div className="dialpad-input">{dialed}</div>
          </section>
          <section className="dialpad-dials-container">{renderDialPadItems()}</section>
        </section>
      </Div>
    </Draggable>
  )
}

const Div = styled.div`
  position: absolute;
  width: ${({ theme }) => theme.util.pixelToRem(228)};
  height: ${({ theme }) => theme.util.pixelToRem(350)};
  border-radius: 0.75rem;
  overflow: hidden;
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  bottom: 1rem;
  right: 2rem;
  transform: none;
  box-shadow: 0px 5px 0.1rem ${({ theme }) => theme.colors.grey.light};

  .dialpad-header {
    cursor: pointer;
    background-color: ${({ theme }) => theme.colors.primary.darker};
    height: 2rem;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    span {
      position: relative;
      right: 1rem;
      color: ${({ theme }) => theme.colors.grey.lightest};
      &:hover {
        color: ${({ theme }) => theme.colors.grey.lighter};
      }
    }
  }

  .dialpad-body {
    background-color: #f2f4f6; // to be updated ... ask designer
    display: flex;
    flex-direction: column;
    flex: 1;

    .dialpad-input-container {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.75rem 0;

      .dialpad-input {
        height: ${({ theme }) => theme.util.pixelToRem(45)};
        width: ${({ theme }) => theme.util.pixelToRem(175)};
        background-color: ${({ theme }) => theme.colors.grey.lightest};
        ${({ theme }) => theme.typography.h5.normal};
        font-weight: ${({ theme }) => theme.fontWeight.normal};
        color: ${({ theme }) => theme.colors.primary.dark};
        border-radius: 0.625rem;
        padding: 0.5rem;
        overflow-y: hidden;
        overflow-x: scroll;
        display: flex;
        align-items: center;
        flex-direction: row-reverse;

        &::selection {
          background-color: ${({ theme }) => theme.colors.primary.dark};
          color: ${({ theme }) => theme.colors.primary.lightest};
        }
      }
    }

    .dialpad-dials-container {
      flex: 1;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: ${({ theme }) => theme.util.pixelToRem(11)};
      padding: ${({ theme }) =>
        `0 ${theme.util.pixelToRem(27)} ${theme.util.pixelToRem(20)} ${theme.util.pixelToRem(27)}`};
    }
  }
`

export { DialPad }
